import { matchRoute } from './routeRegistry'

/**
 * Deriva titulo, trilha e descricao de um pathname.
 *
 * Existe separado do React porque derivacao de cabecalho e calculo sobre string, e calculo
 * sobre string se testa sem DOM, sem provider e sem mock — o que permite o teste que
 * percorre as 98 rotas declaradas no App.tsx.
 *
 * Antes disso, o AppLayout tinha um ROUTE_MAP escrito a mao com 26 entradas para 98 rotas, e
 * toda rota fora dele caia num fallback que imprimia `pathname.slice(1)` como se fosse
 * rotulo — foi o que produziu "Home / admin/organizacoes" em producao.
 */
export interface ItemTrilha {
  label: string
  /** Ausente no ultimo item: a pagina onde se esta nao e link para si mesma. */
  path?: string
}

export interface CabecalhoDePagina {
  titulo: string
  trilha: ItemTrilha[]
  descricao?: string
  /** true quando a rota nao resolveu no registro — o cabecalho e generico de proposito. */
  semRegistro: boolean
  /**
   * true quando a rota so casou por um padrao de prefixo (`/^\/finops/`, `/^\/recursos/`).
   * Ela "resolve", mas com o nome do MODULO no lugar do nome da tela. Uma verificacao que so
   * pergunta "resolveu?" a considera coberta, e ela nao esta.
   */
  cobertaPorPrefixo: boolean
}

/**
 * Onde cada nivel intermediario da trilha aponta.
 *
 * A hierarquia sai do label (`Pai - Filho`), mas label nao carrega URL, e o mapa antigo do
 * AppLayout tinha esses seis niveis CLICAVEIS. Derivar o caminho a partir dos segmentos do
 * pathname erraria: '/enderecamento' tem um segmento e label 'Rede - Enderecamento IP', e
 * '/rede' nao existe.
 *
 * Mapa explicito, com exatamente os seis destinos que ja funcionavam. Nivel fora dele fica
 * sem link — que e o comportamento de hoje para os demais.
 */
const CAMINHO_DO_NIVEL: Record<string, string> = {
  Admin: '/admin/usuarios',
  Chamados: '/chamados',
  FinOps: '/finops',
  Jira: '/integracoes/jira',
  'Notificações': '/notificacoes',
  'Segurança': '/security',
}

/** Nome da aplicacao. Aparece como titulo APENAS quando a rota nao esta no registro. */
export const TITULO_GENERICO = 'ConsoleAI'

/**
 * A hierarquia vem do label, no formato `Pai - Filho`, que 29 dos 55 labels do registro ja
 * seguem: 'Admin - Usuarios', 'FinOps - Recomendacoes', 'Chamados - Relatorios'.
 *
 * Label de um segmento vira trilha de um nivel. Nenhum caso hoje precisa de mais de tres.
 */
function segmentosDoLabel(label: string): string[] {
  return label.split(' - ').map((s) => s.trim()).filter(Boolean)
}

export function routeHeader(pathname: string): CabecalhoDePagina {
  const casado = matchRoute(pathname)
  const info = casado?.info

  if (!info) {
    // Nunca imprimir o pathname. Feio, mas honesto: mostrar 'admin/organizacoes' como rotulo
    // e apresentar caminho de URL como se fosse texto escrito por alguem.
    return {
      titulo: TITULO_GENERICO,
      trilha: [{ label: 'Home', path: '/' }],
      semRegistro: true,
      cobertaPorPrefixo: false,
    }
  }

  const segmentos = segmentosDoLabel(info.label)
  const titulo = segmentos[segmentos.length - 1] ?? info.label

  // O ultimo segmento NAO entra na trilha: ele e o titulo logo abaixo, e trilha e caminho —
  // a pagina onde se esta nao precisa figurar nela. Isso resolve a repeticao tripla sem
  // tocar na barra do topo.
  const intermediarios = segmentos.slice(0, -1)

  return {
    titulo,
    trilha: [
      { label: 'Home', path: '/' },
      ...intermediarios.map((label) => ({ label, path: CAMINHO_DO_NIVEL[label] })),
    ],
    descricao: info.subtitle,
    semRegistro: false,
    cobertaPorPrefixo: !casado!.exata,
  }
}
